import {AxiosError, AxiosResponse, type RawAxiosRequestConfig} from "axios";
import {useRef, useState} from "react";
import Button from 'react-bootstrap/Button';
import FloatingLabel from "react-bootstrap/FloatingLabel";
import Form from 'react-bootstrap/Form';
import Modal from "react-bootstrap/Modal";
import {useTranslation} from "react-i18next";
import {useNavigate} from "react-router-dom";
import {RenderAlert} from "../common/RenderAlert";
import {logger} from "../utils/Logger";
import {StringUtils} from "../utils/StringUtils";
import {Utils} from "../utils/Utils";
import {LogoutForm} from "./LogoutForm";
import {SavedLocation} from "./SavedLocation";
import {XSRF} from "./Xsrf";

interface TfaProps {
    validateOTPFunc : (totp : string, options?: RawAxiosRequestConfig) => Promise<AxiosResponse<void, string>>
}

export const Tfa = ({validateOTPFunc} : TfaProps) =>
{
    /**
     * Navigate is used to navigate to the main page after signup
     */
    const navigate = useNavigate();
    /**
     * The error message if some error occurred which is not a validation error
     */
    const [errorMessage, setErrorMessage] = useState<string>();
    /**
     * The OTP code
     */
    const [totp, setTotp] = useState<string>("");
    // store a reference to the invisible logout submit button which can be used to programmatically submit
    // the logout form when the user selects logout from the menu
    const logoutSubmitButtonRef = useRef<HTMLButtonElement>(null);
    /**
     * i18next translation hook
     */
    const {t} = useTranslation();

    const validateOTP = () =>
    {
        if (totp === undefined) {
            throw Error("totp is undefined");
        }

        // a XSRF cookie is required
        XSRF.generateCookie();

        validateOTPFunc(totp, {
            headers: {
                "TOTP" : totp
            }
        })
        .then(() =>
        {
            navigate(SavedLocation.getSavedLocation()??"/");
        })
        .catch((err: Error | AxiosError) =>
        {
            logger.error("Error validating TOTP", err);

            if (err instanceof AxiosError && err.response?.status === 401) {
                setErrorMessage(t("tfa.invalid-2fa-code"))
            }
            else {
                setErrorMessage(Utils.getErrorMessage(err, t));
            }
        })
    }

    return (
        <Modal show={true} centered={true} animation={false} onHide={() => logoutSubmitButtonRef.current?.click()} backdrop={"static"}>
            <Modal.Header closeButton={true}>
                <Modal.Title>{t("tfa.title")}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <RenderAlert message={errorMessage}/>

                <div>{t("tfa.text")}</div>

                <FloatingLabel label={t('tfa.otp-label')} className="text-secondary mt-3">
                    <Form.Control type="text" name="totp" placeholder="TOTP" onChange={e => setTotp(e.target.value)}/>
                </FloatingLabel>

                <LogoutForm ref={logoutSubmitButtonRef}/>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="primary" type="submit" onClick={validateOTP} disabled={StringUtils.isEmpty(totp)}>
                    {t('tfa.login-button')}
                </Button>
            </Modal.Footer>
        </Modal>
    );
}
